import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { usersAPI } from '../../api';
import { cn, formatDate } from '../../utils';
import { Card, Select, Badge, Spinner, EmptyState } from '../../components/ui';
import { Users, User, Shield } from 'lucide-react';

export const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await usersAPI.list(filter || undefined);
        setUsers(response.data);
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, [filter]);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-gray-500">Manage gym members and staff</p>
        </div>
        <Select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          options={[
            { value: '', label: 'All Roles' },
            { value: 'admin', label: 'Admins' },
            { value: 'trainer', label: 'Trainers' },
            { value: 'client', label: 'Clients' },
          ]}
          className="w-40"
        />
      </div>

      <Card>
        {loading ? (
          <div className="p-8 text-center"><Spinner /></div>
        ) : users.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No users found"
            description="Users will appear here when they register"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">User</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Role</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Joined</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-gray-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={u.role === 'admin' ? 'danger' : u.role === 'trainer' ? 'info' : 'success'}>
                        {u.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={u.is_active !== false ? 'success' : 'danger'}>
                        {u.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Org ID for sharing */}
      <Card className="p-4 bg-primary-50 border-primary-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Shield className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-primary-900">Your Organization ID</p>
            <p className="text-xs text-primary-700 font-mono">{user?.org_id}</p>
            <p className="text-xs text-primary-600 mt-1">Share this with trainers and clients to join your gym</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UsersPage;
